import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Store, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { money0 } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function RefLanding() {
  const { refCode } = useParams();
  const [info, setInfo] = useState(undefined);
  const [plan, setPlan] = useState("basic");
  const [form, setForm] = useState({ client_name: "", client_email: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/ref/${refCode}`).then(({ data }) => setInfo(data)).catch(() => setInfo(null));
  }, [refCode]);

  if (info === undefined) return <div className="min-h-screen bg-xuma-bg grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-xuma-primary" /></div>;
  if (info === null) return (
    <div className="min-h-screen bg-xuma-bg grid place-items-center px-6 text-center">
      <div><h1 className="font-heading font-extrabold text-3xl text-xuma-dark">Enlace no válido</h1>
      <p className="mt-3 text-xuma-muted">Este enlace de referido no existe.</p>
      <Link to="/" className="inline-block mt-6"><Button className="rounded-full bg-xuma-primary text-white">Ir a XUMA</Button></Link></div>
    </div>
  );

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/ref/${refCode}/checkout`, { ...form, plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "No se pudo iniciar el pago"); setSending(false); }
  };

  const plans = Object.entries(info.plans);

  return (
    <div className="min-h-screen bg-xuma-bg">
      <div className="max-w-2xl mx-auto px-5 py-12">
        <div className="flex items-center gap-2 mb-8">
          <span className="h-8 w-8 rounded-lg bg-xuma-primary grid place-items-center text-white font-heading font-extrabold">X</span>
          <span className="font-heading font-extrabold text-xl text-xuma-dark">XUMA</span>
        </div>

        <span className="inline-flex items-center gap-2 rounded-full bg-white border border-xuma-border px-4 py-1.5 text-xs font-medium text-xuma-muted">
          <Store className="h-3.5 w-3.5 text-xuma-primary" /> Referido por {info.distributor_name}
        </span>
        <h1 className="mt-5 font-heading font-extrabold text-3xl sm:text-4xl text-xuma-dark tracking-tight">
          Crea tu tarjeta digital XUMA
        </h1>
        <p className="mt-3 text-xuma-muted">Elige tu plan y registra tus datos. {info.distributor_name} te acompañará en la activación.</p>

        {done ? (
          <div className="mt-10 rounded-3xl bg-white border border-xuma-border p-10 text-center" data-testid="ref-success">
            <div className="h-14 w-14 rounded-full bg-xuma-primary grid place-items-center mx-auto text-white"><Check className="h-7 w-7" /></div>
            <h2 className="mt-5 font-heading font-extrabold text-2xl text-xuma-dark">¡Registro recibido!</h2>
            <p className="mt-2 text-xuma-muted">Gracias. {info.distributor_name} se pondrá en contacto para activar tu tarjeta XUMA.</p>
          </div>
        ) : (
          <>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {plans.map(([key, p]) => (
                <button key={key} data-testid={`ref-plan-${key}`} onClick={() => setPlan(key)}
                  className={`text-left rounded-2xl border p-5 transition-colors ${plan === key ? "border-xuma-primary bg-white ring-1 ring-xuma-primary" : "border-xuma-border bg-white hover:border-xuma-primary/40"}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-heading font-bold text-xuma-dark">{p.label}</p>
                    {plan === key && <Check className="h-4 w-4 text-xuma-primary" />}
                  </div>
                  <p className="mt-2 text-sm text-xuma-muted">{money0(p.config)} config · {money0(p.monthly)}/mes</p>
                </button>
              ))}
            </div>

            <div className="mt-6 rounded-3xl bg-white border border-xuma-border p-6 space-y-4">
              <div><Label className="mb-1.5 block">Tu nombre *</Label>
                <Input data-testid="ref-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
              <div><Label className="mb-1.5 block">Tu correo *</Label>
                <Input data-testid="ref-email" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} /></div>
              <Button data-testid="ref-submit" onClick={submit} disabled={sending || !form.client_name || !form.client_email}
                className="w-full h-12 rounded-2xl bg-xuma-primary hover:bg-xuma-primaryHover text-white gap-2">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Continuar al pago
              </Button>
              <p className="text-xs text-center text-xuma-muted">Pago seguro con Stripe. Se cobra solo la cuota de configuración.</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
